import { computed, observable, set } from 'mobx';
import { Duration, Enums, slugCase, Contract } from 'Mri.Web.Common';
import { LinkedSoftwareUpdateManifest } from './DisplaysStore';

export class ActiveDisplay implements Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel {
    public constructor(model: Mri.Web.Internal.Controllers.Displays.ActiveDisplayModel) {
        set(this, model);
    }

    @observable
    public faces!: number;
    @observable
    public serialNumber!: string;
    @observable
    public modelNumber!: string;
    @observable
    public partNumber!: string;
    @observable
    public partGuid!: string;
    @observable
    public revision!: string;
    @observable
    public description!: string;
    @observable
    public displayGroups!: Mri.DisplayGroups.DisplayGroup[];
    @observable
    public isFavorited!: boolean;
    @observable
    public shippedDate?: Date;
    @observable
    public softwareDisplayType!: string;
    @observable
    public boardType!: Mri.DisplayApi.BoardTypes;
    @observable
    public roleInUnit!: Mri.DisplayApi.RoleInUnit;
    @observable
    public virtualUnit!: boolean;
    @observable
    public unitSerialNumber!: string;
    @observable
    public unitPartNumber!: string;
    @observable
    public unitRevision!: string;
    @observable
    public unitPartGuid!: string;
    @observable
    public account!: string;
    @observable
    public accountName!: string;
    @observable
    public contract!: string;
    @observable
    public uptime?: Duration;
    @observable
    public timezone?: string;
    @observable
    public displayConnectionData?: Mri.Displays.DisplayConnectionData;
    @observable
    public unitLocation?: Mri.Units.UnitLocation;
    @observable
    public contractData?: Mri.Contracts.Contract;
    @observable
    public displaySoftware!: Mri.DisplaySoftware.DisplaySoftware;
    @observable
    public openTicketCount?: number;
    @observable
    public newDisplayEventCount?: number;
    @observable
    public displayAlertCount?: number;
    @observable
    public reportedSerialNumber!: string;
    @observable
    public displayStatus?: Mri.DisplayStatuses.DisplayStatus;
    @observable
    public partStatus?: Mri.PartStatus.PartStatus;
    @observable
    public lastConnectTime?: Date;
    @observable
    public pollTime?: Date;
    @observable
    public displayHealth?: Mri.DisplayStatuses.DisplayHealth;
    @observable
    public displayHealthEventStatus?: Mri.DisplayEvents.DisplayEventStatus;
    @observable
    public backlightStatus?: Mri.DisplayStatuses.BacklightState;
    @observable
    public videoStatus?: Mri.DisplayStatuses.VideoState;
    @observable
    public activeDisplayEvents?: Mri.DisplayEvents.DisplayEvent[];
    @observable
    public displaySoftwareUpdateStatus?: Mri.DisplaySoftware.DisplaySoftwareUpdateStatus;
    @observable
    public linkedSoftwareUpdateManifests!: LinkedSoftwareUpdateManifest[];
    @observable
    public measurements?: Mri.WebApi.Measurement[];
    @observable
    public headLevelPartGuid!: string;
    @observable
    public comments?: string;
    @observable
    public aggregates?: Mri.DisplayAggregates.DisplayAggregates;
    @observable
    public configPoll?: Mri.Config.ApiConfigPoll;
    @observable
    public startDate?: Date;
    @observable
    public endDate?: Date;
    @observable
    public serviceLevel?: Mri.Contracts.ServiceLevel;
    @observable
    public environmentalsStatus?: Mri.WebApi.EnvironmentalsStatus;
    @observable
    public currentTime?: Date;
    @observable
    public powerGovSourceName?: string;
    @observable
    public webApiKickDisabled!: boolean;

    @computed
    public get fullAddress() {
        if (this.unitLocation) {
            let address: string = '';

            if (this.unitLocation.address1 && this.unitLocation.city && this.unitLocation.stateOrProvince) {
                address =
                    this.unitLocation.address1 +
                    '/n ' +
                    this.unitLocation.city +
                    ', ' +
                    this.unitLocation.stateOrProvince;
            } else if (this.unitLocation.city && this.unitLocation.stateOrProvince) {
                address = this.unitLocation.city + ', ' + this.unitLocation.stateOrProvince;
            } else if (this.unitLocation.postalCode && this.unitLocation.country) {
                address = this.unitLocation.postalCode + ', ' + this.unitLocation.country;
            } else if (this.unitLocation.country) {
                address = this.unitLocation.country;
            }

            if (this.unitLocation.description) {
                if (address) address += '\n';

                address += this.unitLocation.description;
            }

            return address;
        }
    }

    @computed
    public get muted() {
        return (
            !this.displayConnectionData ||
            !this.displayConnectionData.active ||
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.OfflineOrInactive ||
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.NeverCommunicated ||
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect ||
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.EngineOffTimedOut ||
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.TimedOut
        );
    }

    @computed
    public get state() {
        if (!this.displayConnectionData || !this.displayConnectionData.active) {
            return 'good';
        } else if (
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online &&
            this.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical
        ) {
            return 'critical';
        } else if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.AcPowerLoss) {
            return 'warning';
        } else if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.RecoveryServer) {
            return 'critical';
        } else if (
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online &&
            this.displayHealth == Mri.DisplayStatuses.DisplayHealth.Warning
        ) {
            return 'warning';
        } else if (
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocException ||
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.SerialNumberMismatch
        ) {
            return 'exception';
        } else if (this.muted) {
            return 'offline';
        } else {
            return 'good';
        }
    }

    @computed
    public get statusMessage() {
        if (!this.displayConnectionData || !this.displayConnectionData.active) return 'Inactive';
        if (!this.displayStatus) return 'Unknown';

        let statusMessage = Enums.toString(Enums.DisplayStatus, this.displayStatus);

        if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online) {
            if (this.displayHealth && this.filteredEvents && this.filteredEvents.length > 0) {
                statusMessage = statusMessage + ', ' + Enums.toString(Enums.DisplayHealth, this.displayHealth);
            }
            if (this.backlightStatus == Mri.DisplayStatuses.BacklightState.Disabled) {
                statusMessage = statusMessage + '\nBacklight Disabled';
            }
            if (this.videoStatus != undefined && this.videoStatus != Mri.DisplayStatuses.VideoState.Valid) {
                statusMessage = statusMessage + '\nVideo ' + Enums.toString(Enums.VideoState, this.videoStatus);
            }
            if (this.filteredEvents && this.filteredEvents.length > 0) {
                const eventDetails = this.filteredEvents.map(e => e.details).join('\n');
                statusMessage = statusMessage + '\n' + eventDetails;
            }
        }

        if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect) {
            statusMessage = "Could Not Connect";
        }

        return statusMessage;
    }

    @computed
    public get statusMessageBrief() {
        if (!this.displayConnectionData || !this.displayConnectionData.active) return 'Inactive';
        if (!this.displayStatus) return 'Unknown';

        let statusMessage = Enums.toString(Enums.DisplayStatus, this.displayStatus);

        if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online) {
            if (this.backlightStatus == Mri.DisplayStatuses.BacklightState.Disabled) {
                statusMessage = statusMessage + ', Backlight Disabled';
            } else if (this.videoStatus != undefined && this.videoStatus != Mri.DisplayStatuses.VideoState.Valid) {
                statusMessage = statusMessage + ', Video ' + Enums.toString(Enums.VideoState, this.videoStatus);
            } else if (this.displayHealth) {
                statusMessage = statusMessage + ', ' + Enums.toString(Enums.DisplayHealth, this.displayHealth);
            }
        }
        
        if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect) {
            statusMessage = "Could Not Connect";
        }
        
        return statusMessage;
    }

    @computed
    public get statusTooltip() {
        if (!this.displayConnectionData || !this.displayConnectionData.active) return 'inactive';
        if (!this.displayStatus) return 'unknown';

        if (this.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online) {
            if (this.activeDisplayEvents?.length && this.activeDisplayEvents.find(de => !!de.downtimeIncidentType)) {
                const firstDTI = this.activeDisplayEvents.find(de => !!de.downtimeIncidentType);
                return slugCase(Enums.toString(Enums.DowntimeIncidentType, firstDTI!.downtimeIncidentType!));
            } else if (this.backlightStatus == Mri.DisplayStatuses.BacklightState.Disabled) {
                return 'backlight-' + slugCase(Enums.toString(Enums.BacklightState, this.backlightStatus));
            } else if (this.videoStatus != undefined && this.videoStatus != Mri.DisplayStatuses.VideoState.Valid) {
                return 'video-' + slugCase(Enums.toString(Enums.VideoState, this.videoStatus));
            } else if (this.displayHealth) {
                return 'health-' + slugCase(Enums.toString(Enums.DisplayHealth, this.displayHealth));
            } else {
                return 'status-' + slugCase(Enums.toString(Enums.DisplayStatus, this.displayStatus));
            }
        } else {
            return 'status-' + slugCase(Enums.toString(Enums.DisplayStatus, this.displayStatus));
        }
    }
    @computed
    public get videoState() {
        if (
            this.displayStatus == Mri.DisplayStatuses.DisplayStatus.Online &&
            (this.displayHealth == Mri.DisplayStatuses.DisplayHealth.Warning ||
                this.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical) &&
            this.backlightStatus != Mri.DisplayStatuses.BacklightState.Disabled &&
            (this.videoStatus == Mri.DisplayStatuses.VideoState.Valid ||
                this.backlightStatus == Mri.DisplayStatuses.BacklightState.StandbyMode)
        ) {
            return 'warning';
        } else if (
            this.backlightStatus != Mri.DisplayStatuses.BacklightState.Disabled &&
            this.videoStatus != Mri.DisplayStatuses.VideoState.NotConnected &&
            (this.videoStatus == Mri.DisplayStatuses.VideoState.Valid ||
                this.backlightStatus == Mri.DisplayStatuses.BacklightState.StandbyMode)
        ) {
            return 'showingVideo';
        } else if (
            this.backlightStatus == Mri.DisplayStatuses.BacklightState.Disabled ||
            this.videoStatus == Mri.DisplayStatuses.VideoState.InternalError ||
            this.videoStatus == Mri.DisplayStatuses.VideoState.InvalidResolution ||
            this.videoStatus == Mri.DisplayStatuses.VideoState.NotConnected
        ) {
            return 'noVideo';
        } else {
            return 'showingVideo';
        }
    }

    @computed
    public get filteredEvents() {
        const filteredEvents = this.activeDisplayEvents?.filter(
            e => e.displayEventType != Mri.DisplayEvents.DisplayEventType.NocCouldNotConnect
        );
        // filteredEvents = filteredEvents?.filter(e => e.downtimeIncidentType != Mri.DisplayApi.DowntimeIncidentTypes.INCIDENT_OFFLINE);

        return filteredEvents;
    }
}
